import type { PortalType } from 'src/lib/repository/portal-layout/portal-layout.model';
import type { IAccount } from './account.model';

/**
 * 로그인된 계정 타입에 맞는 "내 마이페이지" 경로를 반환한다.
 *
 * 우선순위:
 * 1. `agentUser` 객체 또는 `AGENT` role → 마케터 마이페이지
 * 2. `partnerUser` 객체 또는 `PARTNER` role → 파트너 마이페이지 (옵션 B 통합 후 portal-mypage)
 * 3. `userInfo`/미로그인 → 일반 회원 마이페이지 (기본 경로)
 *
 * `roles` fallback: 로그인 role 파라미터에 따라 API가 agentUser/partnerUser
 * 객체를 채우지 않는 경우에도 roles 필드로 올바른 경로를 판별한다.
 *
 * 헤더의 마이페이지 드롭다운과 모바일 사이드 메뉴에서 공유된다.
 *
 * 변경 이력:
 * - 2026-05-04 옵션 B (D5 reference) — partner 마이페이지가 `/my-info/mypage` (legacy
 *   `MypagePageComponent`) 에서 `/portal-mypage` (PortalDynamicLayoutComponent) 로 통합.
 *   portal-mypage 의 `portalType` signal 기본값이 PARTNER 라 query string 없이 진입 가능.
 */
export const DEFAULT_MYPAGE_PATH = '/my-info/customer';

export const AGENT_MYPAGE_PATH = '/marketer/mypage';

export const PARTNER_MYPAGE_PATH = '/portal-mypage';

/**
 * 로그인된 계정이 **속한** 포털 타입을 판별한다 — `portalType` 의 계정 측 SSOT.
 *
 * `/portal-mypage` 는 세 종류 마이페이지를 한 라우트로 통합했고, 어느 것을 그릴지는
 * `?portalType=` 쿼리 하나로 정해진다. 그 쿼리는 URL 에 그대로 남으므로 **계정이 바뀌어도
 * 살아남는다** — 이 함수가 "이 계정이 봐야 하는 값"을 돌려주고
 * {@link portalTypeGuard} 가 URL 값과 대조해 불일치를 바로잡는다.
 *
 * 판별 우선순위는 {@link resolveMyPagePath} 와 **동일**하다(같은 로직을 쓴다):
 * 연관 엔티티(`agentUser` → `partnerUser`) → `roles` fallback → CUSTOMER.
 *
 * @returns 계정이 없으면 `null` (판별 불가 — 호출부가 로그인으로 보낸다)
 */
export function resolveAccountPortalType(
  account?: IAccount | null,
): PortalType | null {
  if (!account) return null;

  // Primary: 연관 엔티티 객체로 판별
  if (account.agentUser) return 'MARKETER';
  if (account.partnerUser) return 'PARTNER';

  // Fallback: roles 필드로 판별 (엔티티가 null이어도 role은 항상 설정됨)
  const { roles } = account;
  if (roles?.includes('AGENT')) return 'MARKETER';
  if (roles?.includes('PARTNER')) return 'PARTNER';

  return 'CUSTOMER';
}

export function resolveMyPagePath(account?: IAccount | null): string {
  switch (resolveAccountPortalType(account)) {
    case 'MARKETER':
      return AGENT_MYPAGE_PATH;
    case 'PARTNER':
      return PARTNER_MYPAGE_PATH;
    default:
      // CUSTOMER 및 계정 없음(null)
      return DEFAULT_MYPAGE_PATH;
  }
}
